import styled from 'styled-components';

import { LIGHT_GREY, VL_BLACK, WHITE } from '../../../utils/colours';
import { isMobileApp } from '../../../utils/getHost';

const borderRadius = '10px';

// Screens this width or height in px and below will show mobile styles
// We want consistent experience in either orientation as rotating to landscape is used to get media fullscreen
// Always show mobile styles in mobile app
const MOBILE_BREAKPOINT = isMobileApp ? Number.MAX_SAFE_INTEGER : 700;

const MOBILE_HEIGHT_BREAKPOINT = isMobileApp ? Number.MAX_SAFE_INTEGER : 400;

export const ModalPlayerContainer = styled.div`
    width: 80vw;
    max-height: 95vh;
    min-width: 400px;
    max-width: 98vw;
    overflow-y: auto;

    /* If video size was only based off screen width, then this component would be too tall in wide short windows */
    @media (max-height: 60vw) {
        width: calc((90vh - 200px) * (16 / 9));
    }

    ${({ $scrollableContent }) => $scrollableContent && `
        overflow: hidden;
        display: grid;
        grid-template-rows: auto auto minmax(40px, 1fr);
    `}

    @media (max-width: ${MOBILE_BREAKPOINT}px), (max-height: ${MOBILE_HEIGHT_BREAKPOINT}px) {
        width: 100%;
        height: 100%;
        max-width: none;
        max-height: none;
        min-width: auto;
        min-height: auto;
        overflow: hidden;
        display: grid;
        grid-template-rows: auto minmax(0, 1fr) auto;
        grid-template-columns: 100%;
        background: #000;

        // Only media content shown on landscape mobile
        @media (orientation: landscape) and (pointer: coarse) {
            grid-template-rows: 0px 100%;
            grid-template-columns: 100%;
        }
    }
`;

ModalPlayerContainer.displayName = 'ModalPlayerContainer';


export const ModalPlayerHeader = styled.div`
    display: grid;
    align-items: center;
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-rows: auto;
    grid-template-areas:
        "title close";
    padding: 15px 20px;
    border-radius: ${borderRadius} ${borderRadius} 0 0;
    background: ${({ $backgroundColour }) => $backgroundColour};
    color: ${({ $textColour }) => $textColour ?? '#fff'};
    font-weight: 100;
    font-size: 1.5em;
    gap: 15px;

    @media (max-width: ${MOBILE_BREAKPOINT}px), (max-height: ${MOBILE_HEIGHT_BREAKPOINT}px) {
        border-radius: 0;
        background: none;
        color: #fff;
        grid-template-columns: 1fr auto;
        grid-template-rows: auto;

        @media (orientation: landscape) and (pointer: coarse) {
            background: none;
        }
    }
`;

ModalPlayerHeader.displayName = 'ModalPlayerHeader';


export const ModalPlayerTitle = styled.h2`
    margin: 0;
    font-size: 0.9em;
    color: inherit;
    grid-area: title;

    @media (max-width: ${MOBILE_BREAKPOINT}px), (max-height: ${MOBILE_HEIGHT_BREAKPOINT}px) {
        @media (orientation: landscape) and (pointer: coarse) {
            display: none;
        }
    }

`;

ModalPlayerTitle.displayName = 'ModalPlayerTitle';


export const ModalPlayerCloseButton = styled.button`
    background: none;
    border: none;
    height: 20px;
    width: 20px;
    padding: 0;
    cursor: pointer;
    grid-area: close;

    svg {
        height: 100%;
        width: 100%;

        rect {
            fill: ${({ $colour }) => isMobileApp ? '#fff' : $colour ?? '#fff'};
        }

        @media (max-width: ${MOBILE_BREAKPOINT}px) {
            rect {
                fill: #fff;
            }
        } 
    }
    @media (max-width: ${MOBILE_BREAKPOINT}px), (max-height: ${MOBILE_HEIGHT_BREAKPOINT}px) {
        @media (orientation: landscape) and (pointer: coarse) {
            z-index: 10;
            position: absolute;
            rect {
                fill: #fff !important;
            }
            @media (pointer: coarse) {
                top: 15px;
                right: 20px;
            }
            @media (pointer: fine) {
                top: 5px;
                right: 5px;
            }
        }
    }

    
`;

ModalPlayerCloseButton.displayName = 'ModalPlayerCloseButton';


// Currently only show pagination buttons on mobile
export const ModalPlayerPaginationButtonsContainer = styled.div`
    gap: 10px;
    display: none;

    @media (max-width: ${MOBILE_BREAKPOINT}px), (max-height: ${MOBILE_HEIGHT_BREAKPOINT}px) {
        display: flex;
        /** center inside footer */
        width: 100%;
        justify-content: center;
        gap: 30px;
    }
`;

ModalPlayerPaginationButtonsContainer.displayName = 'ModalPlayerPaginationButtonsContainer';


export const ModalPlayerPaginationButton = styled.button`
    background: ${WHITE(25)};
    border-radius: 50%;
    border: none;
    padding: 10px;
    color: currentColor;
    width: 45px;
    height: 45px;
    cursor: pointer;

    svg {
        width: auto;
        height: 100%;
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

ModalPlayerPaginationButton.displayName = 'ModalPlayerPaginationButton';


const DESKTOP_CONTENT_MAX_HEIGHT_VH = 72;

export const ContentWrapper = styled.div`
    width: 100%;
    max-height: ${DESKTOP_CONTENT_MAX_HEIGHT_VH}vh;
    background-color: #000;
    overflow: hidden;
    display: flex;
    justify-content: center;
    
	@media (max-width: ${MOBILE_BREAKPOINT}px), (max-height: ${MOBILE_HEIGHT_BREAKPOINT}px) {
        height: 100%;
        align-items: center;

        /* Take the space left by title/footer, never more, so tall images can't overflow */
        ${({ $fitMedia }) => $fitMedia && `
            flex: 1 1 auto;
            height: auto;
            min-height: 0;
            max-height: none;
        `}
    }

    @media (orientation: landscape) and (pointer: coarse) {
        max-height: 100%;
    }
`;

ContentWrapper.displayName = 'ContentWrapper';

export const ContentTitle = styled.div`
    padding: 0 0 5px 5px;
    display: none;

    @media (max-width: ${MOBILE_BREAKPOINT}px), (max-height: ${MOBILE_HEIGHT_BREAKPOINT}px) {
        display: flex;

        @media (orientation: landscape) and (pointer: coarse) {
            display: none;
        }
    }
`;
ContentTitle.displayName = 'ContentTitle';

export const ContentContainer = styled.div`
    display: grid;
    grid-template-rows: min-content min-content min-content;
    grid-template-columns: auto;
    align-content: center;
	@media (max-width: ${MOBILE_BREAKPOINT}px), (max-height: ${MOBILE_HEIGHT_BREAKPOINT}px) {
        height: 100%;
        max-height: 100%;
        align-items: center;

        ${({ $fitMedia }) => $fitMedia && `
            display: flex;
            flex-direction: column;
            align-items: stretch;
            min-height: 0;
            overflow: hidden;
        `}

        @media (orientation: landscape) and (pointer: coarse) {
            grid-template-rows: 100%;
        }
    }

`;
ContentContainer.displayName = 'ContentContainer';

export const ContentFooter = styled.div`
    display: none;
    flex-direction: column;
    justify-content: center;
    gap: 10px;
    padding: 0 10px;

    max-width: 100%;
    overflow: auto;

    @media (max-width: ${MOBILE_BREAKPOINT}px), (max-height: ${MOBILE_HEIGHT_BREAKPOINT}px) {
        display: flex;
        @media (orientation: landscape) and (pointer: coarse) {
            display: none;
        }
    }

`;
ContentFooter.displayName = 'ContentFooter';


export const VideoContainer = styled.div`
    width: 100%;
    max-height: inherit;
    max-width: ${DESKTOP_CONTENT_MAX_HEIGHT_VH * 16/9}vh; /* Based on max height of content and aspect ratio of 16:9 */
    aspect-ratio: 16 / 9;
    position: relative;

    @media (max-width: ${MOBILE_BREAKPOINT}px), (max-height: ${MOBILE_HEIGHT_BREAKPOINT}px) {
        max-width: none;

        @media (orientation: landscape) and (pointer: coarse) {
            height: 100%;
            max-height: 100%;
        }

    }
`;

VideoContainer.displayName = 'VideoContainer';


// Render image container according to image aspect ratio but restricted by height if needed
// With `$fill`, the container fills its parent instead and the image is letterboxed inside it (object-fit: contain)
export const ZoomableImageContainer = styled.div.attrs(({ $imageWidth, $imageHeight, $fill }) => {
    return {
        style: $fill ? undefined : {
            aspectRatio: `${$imageWidth ?? 0} / ${$imageHeight ?? 0}`
        }
    };
})`
    max-height: inherit;
    width: 100%;
    position: relative;
    ${({ $fill }) => $fill && `
        height: 100%;
        max-height: 100%;
    `}

    // Copied from VideoPlayer for snapshots
    ::before {
        content: "";
        background: black;
        opacity: 0;
        pointer-events: none;
        z-index: 10;
        display: block;
        position: absolute;
        height: 100%;
        width: 100%;
        top: 0;
        left: 0;
        transition: opacity 0.08s ease;
    }

    ${({ $snapshotOverlay }) => $snapshotOverlay && `
        ::before {
            opacity: 0.3;
        }
    `}
`;

ZoomableImageContainer.displayName = 'ZoomableImageContainer';


export const ImageElement = styled.img`
    object-fit: contain;

    // Override CSS applied by react-zoom-pan-pinch so image can still be right clicked
    pointer-events: auto !important;
`;

ImageElement.displayName = 'ImageElement';


// Wraps image loading spinner or image error message
// Ensures they both maintain height
export const MediaPlaceholder = styled.div`
    aspect-ratio: 16 / 9;
    width: 100%;
    max-height: 100%;
    // Relative position needed by absolutely positioned loading spinner
    position: relative;
`;

MediaPlaceholder.displayName = 'MediaPlaceholder';


export const MediaErrorMessageContainer = styled.div`
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding: 20px 30px;
    text-align: center;
    overflow-y: auto;

    h2, p {
        color: #fff;
    }
`;

MediaErrorMessageContainer.displayName = 'MediaErrorMessageContainer';


export const ModalPlayerFooter = styled.div`
    background: ${LIGHT_GREY};
    color: ${VL_BLACK()};
    border-radius: 0 0 ${borderRadius} ${borderRadius};
    padding: 10px;
    column-gap: 15px;
    row-gap: 5px;
    overflow: auto;

    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-rows: min-content auto;
    grid-template-areas:
        'left right'
        'content content';

    @media (max-width: ${MOBILE_BREAKPOINT}px), (max-height: ${MOBILE_HEIGHT_BREAKPOINT}px) {
        display: flex;
        align-items: flex-end;
        background: none;
        color: #fff;

        ${({ $mobileFullBleed }) => $mobileFullBleed && `
            display: block;
            padding: 0;
            border-radius: 0;
            overflow: visible;
            color: ${VL_BLACK()};
        `}

        @media (orientation: landscape) and (pointer: coarse) {
            display: none;
        }
    }
`;

ModalPlayerFooter.displayName = 'ModalPlayerFooter';

export const ModalPlayerFooterLeft = styled.div`
    grid-area: left;
    display: flex;
    gap: 5px;
    align-items: center;
    font-weight: 100;
    overflow: hidden;

    @media  (max-width: ${MOBILE_BREAKPOINT}px), (max-height: ${MOBILE_HEIGHT_BREAKPOINT}px) {
        display: none;
    }
`;

ModalPlayerFooterLeft.displayName = 'ModalPlayerFooterLeft';

export const ModalPlayerFooterActions = styled.div`
    grid-area: right;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 6px;
    overflow: hidden;

    @media (max-width: ${MOBILE_BREAKPOINT}px), (max-height: ${MOBILE_HEIGHT_BREAKPOINT}px) {
        display: none;
    }
`;

ModalPlayerFooterActions.displayName = 'ModalPlayerFooterActions';

export const ModalPlayerFooterContent = styled.div`
    grid-area: content;

    /* Use margin top here rather than gap on ModalPlayerFooter in case ModalPlayerFooterContent isn't used. */
    margin-top: 15px;
    h3 {
        color: inherit;
    }


    @media  (max-width: ${MOBILE_BREAKPOINT}px), (max-height: ${MOBILE_HEIGHT_BREAKPOINT}px) {
        display: none;

        @media (orientation: landscape) and (pointer: coarse) {
            display: none;
        }
    }
`;


export const ModalPlayerVehicleAnalytics = styled.div`
    @media  (max-width: ${MOBILE_BREAKPOINT}px), (max-height: ${MOBILE_HEIGHT_BREAKPOINT}px) {
        display: none;

        @media (orientation: landscape) and (pointer: coarse) {
            display: none;
        }
    }
`;

ModalPlayerVehicleAnalytics.displayName = 'ModalPlayerVehicleAnalytics'

ModalPlayerFooterContent.displayName = 'ModalPlayerFooterContent';
